/**
 * Logo Component
 * 
 * Project: SunMarbella Holiday Rental Website
 * Version: 1.0.0
 * Last Modified: 2025-03-09
 * 
 * Purpose:
 * Renders SunMarbella logo in various formats (full or icon) and colors (white or colored).
 * Used in Header, Footer, and branding contexts throughout the application.
 * 
 * Features:
 * - Flexible variant support (full logo or icon-only)
 * - Color variants (white for dark backgrounds, colored for light backgrounds)
 * - Responsive sizing via className prop
 * - SVG-based assets for crisp rendering at any scale
 * - Accessible alt text for screen readers
 * 
 * Props:
 * - variant?: 'full' | 'icon' | 'white' | 'colored' (default: 'full')
 * - color?: 'white' | 'colored' (default: 'white')
 * - className?: string (for sizing and spacing)
 * 
 * Dependencies:
 * - React 19.2.4 (JSX rendering)
 * - SVG assets in src/assets/logos/
 * 
 * Usage Examples:
 * - <Logo /> - Full white logo (default)
 * - <Logo variant="icon" className="w-8 h-8" /> - Icon variant
 * - <Logo color="colored" className="w-48" /> - Full colored logo
 * 
 * Assets:
 * - sunmarbella-full-logo-white.svg
 * - sunmarbella-full-logo-color.svg
 * - sunmarbella-icon-white.svg
 * - sunmarbella-icon-color.svg
 * 
 * Changelog:
 * - 1.0.0 (2025-03-09): Initial implementation with variant and color support
 */

import React, { useMemo } from 'react';

const LOGO_ASSETS = {
  full: {
    white: new URL('../assets/logos/sunmarbella-full-logo-white.svg', import.meta.url).href,
    colored: new URL('../assets/logos/sunmarbella-full-logo-color.svg', import.meta.url).href,
  },
  icon: {
    white: new URL('../assets/logos/sunmarbella-icon-white.svg', import.meta.url).href,
    colored: new URL('../assets/logos/sunmarbella-icon-color.svg', import.meta.url).href,
  },
} as const;

interface LogoProps {
  variant?: 'full' | 'icon' | 'white' | 'colored';
  color?: 'white' | 'colored';
  className?: string;
}

export const Logo: React.FC<LogoProps> = ({ 
  variant = 'full', 
  color = 'white',
  className = '' 
}) => {
  const normalizedVariant = variant === 'icon' ? 'icon' : 'full';
  const normalizedColor = color ?? (variant === 'colored' ? 'colored' : 'white');

  // Memoize the src to prevent re-renders and flashing
  const src = useMemo(() => {
    return LOGO_ASSETS[normalizedVariant][normalizedColor];
  }, [normalizedVariant, normalizedColor]);
  
  const alt = normalizedVariant === 'icon' ? 'SUNMARBELLA icon' : 'SUNMARBELLA';

  return (
    <img 
      src={src}
      alt={alt}
      className={`block ${className}`}
    />
  );
};
